import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Popper } from '@mui/material'
import { ChevronDown, Search } from 'lucide-react'
import Input from '../../ui/Input'
import Select from '../../ui/Select'
import type { AppendixCodeItemOption } from '../../../types/codeIndex'
import type { AppendixColumnDraft } from '../../../utils/appendixTable'

type AppendixCellInputProps = {
  column: AppendixColumnDraft
  codeItem: AppendixCodeItemOption | null
  value: string
  invalid?: boolean
  disabled?: boolean
  ariaLabel: string
  onChange: (value: string, validateNow?: boolean) => void
  onBlur: () => void
}

const INVALID_CELL_CLASS = '!bg-[#FFF5F5]'

const popperModifiers = [
  { name: 'offset', options: { offset: [0, 4] } },
  { name: 'preventOverflow', options: { padding: 12 } },
  { name: 'flip', options: { padding: 12 } },
]

const CodeItemValueSelector: React.FC<AppendixCellInputProps> = ({
  codeItem,
  value,
  invalid = false,
  disabled = false,
  ariaLabel,
  onChange,
  onBlur,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const listboxId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const values = useMemo(() => codeItem?.values || [], [codeItem])

  const filteredValues = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return values
    return values.filter(option =>
      option.description.toLowerCase().includes(query) || option.code.toLowerCase().includes(query)
    )
  }, [searchQuery, values])

  useEffect(() => {
    if (!isOpen) return undefined

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return
      setIsOpen(false)
      setSearchQuery('')
      onBlur()
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onBlur])

  const select = (code: string) => {
    onChange(code, true)
    setIsOpen(false)
    setSearchQuery('')
  }

  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        type="button"
        role="combobox"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-expanded={isOpen}
        aria-invalid={invalid || undefined}
        disabled={disabled || !codeItem}
        onClick={() => setIsOpen(current => !current)}
        className={`flex w-full items-center justify-between gap-1 rounded border px-2 py-1.5 text-left text-sm outline-none transition-colors disabled:cursor-not-allowed disabled:bg-[#F8FAFC] ${invalid ? `border-[#B3261E] ${INVALID_CELL_CLASS}` : isOpen ? 'border-[#1D74D8] bg-white ring-1 ring-[#1D74D8]' : 'border-[#CBD5E1] bg-white hover:border-[#1D74D8]'}`}
      >
        <span className={`truncate ${value ? 'text-gray-900' : 'text-gray-400'}`}>{value || 'Select'}</span>
        <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      <Popper
        open={isOpen}
        anchorEl={containerRef.current}
        placement="bottom-start"
        modifiers={popperModifiers}
        sx={{ zIndex: 1600, minWidth: Math.max(containerRef.current?.offsetWidth || 0, 220) }}
      >
        <div ref={menuRef} className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
          {values.length > 6 ? (
            <div className="border-b border-gray-100 p-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search"
                  aria-label="Search values"
                  className="w-full rounded border border-gray-200 py-1.5 pl-8 pr-2 text-sm focus:border-[#1D74D8] focus:outline-none"
                  autoFocus
                />
              </div>
            </div>
          ) : null}
          <div id={listboxId} role="listbox" aria-label={`${ariaLabel} values`} className="max-h-56 overflow-y-auto py-1">
            {filteredValues.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No values</div>
            ) : (
              filteredValues.map(option => (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={option.code === value}
                  onClick={() => select(option.code)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50 ${option.code === value ? 'bg-blue-50 text-[#1D74D8]' : 'text-gray-700'}`}
                >
                  <span className="min-w-0 flex-1 truncate">{option.description || option.code}</span>
                  <span className="flex-shrink-0 text-xs text-gray-400">{option.code}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </Popper>
    </div>
  )
}

export const AppendixCellInput: React.FC<AppendixCellInputProps> = (props) => {
  const { column, value, invalid = false, disabled = false, ariaLabel, onChange, onBlur } = props

  if (column.dataType === 'CodeItem') {
    return <CodeItemValueSelector {...props} />
  }

  if (column.dataType === 'Boolean') {
    return (
      <Select
        value={value}
        invalid={invalid}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => onChange(event.target.value, true)}
        onBlur={onBlur}
        className={invalid ? INVALID_CELL_CLASS : ''}
      >
        <option value="">Select</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </Select>
    )
  }

  return (
    <Input
      value={value}
      invalid={invalid}
      disabled={disabled}
      aria-label={ariaLabel}
      inputMode={column.dataType === 'Number' ? 'decimal' : undefined}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      className={invalid ? INVALID_CELL_CLASS : ''}
    />
  )
}

export default AppendixCellInput
